import {
  Copy, Duplicate, Plus, Trash,
} from '@styled-icons/boxicons-regular';
import { FormatClear } from '@styled-icons/material';
import { isCollapsed } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo } from 'react';
import Tooltip from '../../../../components/Tooltip';
import { canInsertBlock, canRemoveBlock, getAddNewBlockPath } from '../../../../editorTools/queries';
import {
  addNewBlock,
  copyBlocks,
  deleteBlock,
  duplicateBlock,
} from '../../../../editorTools/transforms';
import { removeStyleAtSelection } from '../../../../nodes/text/transforms';
import { useTPlateEditorRef } from '../../../../plate';
import { useSelectedElementEntry } from '../../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../../plugins/store';
import { getCmdCtrlSymbol } from '../../../../utils/cmdCtrl';
import toolbarStyles from '../../SideToolbar.module.css';
import styles from './Footer.module.css';

function Footer() {
  const editor = useTPlateEditorRef();
  const cmdCtrlSymbol = getCmdCtrlSymbol();
  const selectedElementEntry = useSelectedElementEntry();
  const isClearFormattingDisabled = useDreamEditorStore(
    (state) => {
      const selection = state.editor?.selection;
      return !selection || isCollapsed(selection);
    },
  );

  const path = selectedElementEntry?.[1];
  const addNewBlockPath = getAddNewBlockPath(editor);

  const isAddNewBlockButtonDisabled = !canInsertBlock(editor, addNewBlockPath);
  const isDuplicateBlockButtonDisabled = !(path && canInsertBlock(editor, path));
  const isDeleteBlockButtonDisabled = !(path && canRemoveBlock(editor, path));

  return (
    <div className={styles.footer}>
      {selectedElementEntry && (
        <div className={toolbarStyles.section}>
          <span className={toolbarStyles.label}>Utilities</span>
          <div className="is-flex mb-2">
            <Tooltip center="Copy" right={`${cmdCtrlSymbol} C`}>
              <button
                type="button"
                className={toolbarStyles.toggleButton}
                onClick={() => copyBlocks(editor)}
              >
                <Copy size={20} />
              </button>
            </Tooltip>
            <Tooltip center="Duplicate" right={`${cmdCtrlSymbol} D`}>
              <button
                type="button"
                className={toolbarStyles.toggleButton}
                onClick={() => duplicateBlock(editor)}
                disabled={isDuplicateBlockButtonDisabled}
              >
                <Duplicate size={20} />
              </button>
            </Tooltip>
            <Tooltip center="Delete">
              <button
                type="button"
                className={toolbarStyles.toggleButton}
                onClick={() => deleteBlock(editor)}
                disabled={isDeleteBlockButtonDisabled}
              >
                <Trash size={20} />
              </button>
            </Tooltip>
            <Tooltip center="Clear Formatting">
              <button
                type="button"
                className={toolbarStyles.toggleButton}
                disabled={isClearFormattingDisabled}
                onClick={() => removeStyleAtSelection(editor)}
              >
                <FormatClear size={20} />
              </button>
            </Tooltip>
          </div>
        </div>
      )}
      <div className={toolbarStyles.section}>
        <button
          className={classNames(toolbarStyles.button, toolbarStyles.buttonAddBlock)}
          type="button"
          onClick={() => addNewBlock(editor, addNewBlockPath)}
          disabled={isAddNewBlockButtonDisabled}
        >
          <Plus size={16} className="mr-1" />
          <span>Add New Block</span>
        </button>
      </div>
    </div>
  );
}

export default memo(Footer);
